/*
Client-side PREVIEW of what an order's campaign rewards become once a seller revises the quantities
in a change request.

WHY THIS EXISTS. The seller edits quantities on the order-detail screen and the offer strips have to
follow along there and then - a line dropped to 0 must lose its gift immediately, and a trimmed line
must show the reduced coupon - rather than only after the request is submitted and the page reloaded.
That is a preview, and a preview can only run on the client.

WHAT IT IS NOT. It is not the authority on any of these figures. Nothing here is ever sent back to
the server: the change request submits quantities and nothing else, and the API recomputes every
reward from the STORED order in recalcCouponsOnRevisedOrder. If this file and the API ever disagree,
the API is right and this is a display bug.

MIRRORS, and must be kept in step with:
    medimny-api/lib/campaignBudget.js        lineGrossInclGst, lineExGst
    medimny-api/models/productCoupon.js      computeCashback
    medimny-api/controllers/order.js         grantedOn / perUnitCoupon, and the two value tiers
                                             in recalcCouponsOnRevisedOrder

The terms these functions need (`per_unit` on a coupon block, `terms` on a seller/platform block)
are supplied by the API in the order-detail payload - see campaignTerms in controllers/order.js.
When they are absent the preview helpers fall back to the stored figure, so an order whose campaign
has since been deleted shows what it was granted instead of silently reading zero.
*/

// Money rounds to paise, exactly as the API's round2 does.
export const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

/*
Units this line is charged for, including any free ones a bonus scheme on the line's OWN listing
adds. Mirrors lib/campaignBudget.js - a "Same"/"SameAndDiscount" bonus adds units to the cost base,
a bonus pointing at a different listing does not.
*/
const chargeableUnits = (line) => {
    const qty = Number(line.quantity) || 0;
    const disc = line.discount_on_product;
    const invId = String(line.inventory_id && (line.inventory_id._id || line.inventory_id));
    const bonusOnSelf = disc && disc.purchase && String(disc.inventory_id) === invId;
    return bonusOnSelf ? qty + ((qty / disc.purchase) * disc.bonus) : qty;
};

const unitPrice = (line) => Number(line.ePTR) || Number(line.PTR) || 0;

// Ex-GST value of one line. The basis both value tiers gate their minimum order value on, and the
// basis a percentage cashback is charged on.
export const lineExGst = (line) => chargeableUnits(line) * unitPrice(line);

// Incl-GST value of one line - the ceiling a product coupon is capped to, so the payable can never
// go negative.
export const lineGrossInclGst = (line) => {
    const value = lineExGst(line);
    return value + (value / 100 * (Number(line.GST) || 0));
};

// Gross ex-GST goods value of the revised order. Lines the seller has zeroed contribute nothing -
// the API drops them from the order entirely before it recalculates.
export const orderGrossExGst = (lines) =>
    round2((lines || []).reduce((sum, line) => (
        Number(line && line.quantity) > 0 ? sum + lineExGst(line) : sum
    ), 0));

/*
Gross INCL-GST value of the revised order, BEFORE any product coupon comes off. This is the figure
the API stores as `base_total_amount`, and the one shown struck through on the order-detail screen.

Mirrors `inclGstTotal` in recalcCouponsOnRevisedOrder: summed over the surviving lines only, and
rounded once at the end rather than line by line.
*/
export const orderGrossInclGst = (lines) =>
    round2((lines || []).reduce((sum, line) => (
        Number(line && line.quantity) > 0 ? sum + lineGrossInclGst(line) : sum
    ), 0));

/*
The order's TOTAL product coupon at the revised quantities - what the API stores as
`coupon_discount`.

Each line is prorated and capped by previewLineCoupon, rounded per line exactly as the API's
grantedOn does, and the sum is rounded once more, matching `couponRounded`. Doing the rounding in
the same order as the API is what keeps the two from drifting a paisa apart on a long order.
*/
export const previewOrderCoupon = (lines) =>
    round2((lines || []).reduce((sum, line) => (
        Number(line && line.quantity) > 0 ? sum + previewLineCoupon(line, line.productlevel) : sum
    ), 0));

/*
What the buyer actually pays for the revised order - the API's `total_amount`:

    payable = gross incl-GST value  -  product coupon

and nothing else comes off. NEITHER cashback reduces the payable (both are credited to the buyer's
MediWallet on delivery), and a gift costs the buyer nothing, so trimming a line below a campaign's
minimum changes the offer strips without moving this figure by anything but the goods.

Mirrors P0 in recalcCouponsOnRevisedOrder, clamp included: a coupon can outweigh what is left of a
heavily trimmed line, and the payable must read 0 rather than a negative number.
*/
export const previewPayable = (lines) =>
    Math.max(0, round2(orderGrossInclGst(lines) - previewOrderCoupon(lines)));

/*
What one line's PRODUCT COUPON is worth at the revised quantity.

Prorated at the per-unit rate, then held under two ceilings - what is already reserved for this line,
and what the line is now worth. Identical to grantedOn in the API, and for the same reasons: a
revision may only ever shrink the grant, and the payable must never go negative.
*/
export const previewLineCoupon = (line, block) => {
    if (!block || block.type !== 'coupon') return 0;
    const granted = Number(block.discount_given) || 0;
    const qty = Number(line && line.quantity) || 0;
    if (qty <= 0) return 0;
    // No per-unit rate (an older payload, or a campaign since deleted) - nothing to prorate with, so
    // show what was granted rather than inventing a figure.
    if (block.per_unit === undefined || block.per_unit === null) return round2(granted);
    return round2(Math.min(
        (Number(block.per_unit) || 0) * qty,
        granted,
        lineGrossInclGst(line)
    ));
};

// A product GIFT is granted on the line as a whole, not per lot: any positive quantity keeps it in
// full, and zeroing the line drops it. Mirrors the product-tier branch of the API's recalc.
export const previewLineGift = (line, block) => {
    if (!block || block.type !== 'gift') return '';
    return Number(line && line.quantity) > 0 ? (block.description || '') : '';
};

/*
What a value-tier campaign pays on a given order value. A direct mirror of computeCashback in
medimny-api/models/productCoupon.js, including its rounding to whole rupees - a cashback is a
payment in its own right, and paise on it are noise.

Returns 0 when the order does not clear the campaign's minimum order value, which is what revokes a
cashback the moment a change request takes the order below the threshold.
*/
export const computeCashback = (terms, basis) => {
    if (!terms) return 0;
    const base = Number(basis) || 0;
    if (base < (Number(terms.min_order_value) || 0)) return 0;

    const pct = (Number(terms.cashback_percent) || 0) / 100;
    let raw;
    switch (terms.cashback_type) {
        case 'flat':
            raw = Number(terms.cashback_value) || 0;
            break;
        case 'proportionate':
            raw = base * pct;
            if (Number(terms.cashback_max) > 0) raw = Math.min(raw, Number(terms.cashback_max));
            break;
        case 'minimum':
            // percentage, but never below the guaranteed floor and never above the ceiling
            raw = Math.max(base * pct, Number(terms.cashback_floor) || 0);
            if (Number(terms.cashback_max) > 0) raw = Math.min(raw, Number(terms.cashback_max));
            break;
        default:
            return 0;
    }
    return Math.round(raw);
};

/*
An order-level CASHBACK (seller or platform tier) at the revised order value.

Clamped to what was already allocated, mirroring the API: a revision may only ever shrink the
reward, because topping it up would need a fresh reservation against a campaign cap that another
order may since have taken.
*/
export const previewOrderCashback = (block, grossExGst) => {
    if (!block || block.type !== 'cashback') return 0;
    const allocated = Number(block.cashback_allocated) || 0;
    if (!block.terms) return allocated;   // no terms to preview with - show what was allocated
    return Math.round(Math.min(computeCashback(block.terms, grossExGst), allocated));
};

// An order-level GIFT survives only while the revised order still clears the campaign's minimum
// order value - the same gate the API applies before it revokes the gift and refunds its budget.
export const previewOrderGift = (block, grossExGst) => {
    if (!block || block.type !== 'gift') return '';
    if (!block.terms) return block.description || '';
    return grossExGst >= (Number(block.terms.min_order_value) || 0) ? (block.description || '') : '';
};
